import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, LinearProgress } from '@mui/material';
import { getHomeRouteForRole } from './utils/homeRoute';
const NAVY = '#2f3542';
const REDIRECT_SECONDS = 5;

const Unauthorized = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ProtectedRoute can pass a reason via navigation state so this page shows
  // the right message and redirect target. Defaults to the original
  // "session expired" behaviour for any caller that doesn't specify one,
  // so existing call sites are unaffected.
  const reason = location.state?.reason || 'session_expired';
  const isAccessDenied = reason === 'access_denied';
  const homeRoute = getHomeRouteForRole(location.state?.userRole);

  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);

  useEffect(() => {
    // Access-denied redirects don't auto-navigate — the person's session is
    // fine, there's nothing to "wait out", and ping-ponging them through
    // /login only to be denied again on the same URL is confusing, not helpful.
    if (isAccessDenied) return;

    const interval = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          navigate('/login');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [navigate, isAccessDenied]);

  const progress = ((REDIRECT_SECONDS - secondsLeft) / REDIRECT_SECONDS) * 100;

  const title = isAccessDenied ? "You don't have access to this" : 'Your session has expired';
  const subtitle = isAccessDenied
    ? "This section isn't available for your account role. Contact your account owner if you need access."
    : `You'll be redirected to the login page in ${secondsLeft} second${secondsLeft !== 1 ? 's' : ''}.`;
  const primaryLabel = isAccessDenied ? 'Go to dashboard' : 'Sign in now';
  const handlePrimary = () => navigate(isAccessDenied ? homeRoute : '/login');

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f8f9fa',
        px: 2,
      }}
    >
      {/* Logo mark */}
      <Box
        sx={{
          width: 56,
          height: 56,
          bgcolor: NAVY,
          borderRadius: '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
        }}
      >
        <Typography sx={{ color: 'white', fontWeight: 800, fontSize: 28, lineHeight: 1 }}>
          S
        </Typography>
      </Box>
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, color: NAVY, mb: 1, textAlign: 'center' }}
      >
        {title}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 4, textAlign: 'center', maxWidth: 380 }}
      >
        {subtitle}
      </Typography>
      {/* Progress bar — only meaningful for the auto-redirect (session expiry) case */}
      {!isAccessDenied && (
        <Box sx={{ width: '100%', maxWidth: 360, mb: 4 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 4,
              borderRadius: 2,
              bgcolor: '#e0e0e0',
              '& .MuiLinearProgress-bar': { bgcolor: NAVY },
            }}
          />
        </Box>
      )}
      {/* Actions */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', maxWidth: 360 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={handlePrimary}
          sx={{
            py: 1.5,
            bgcolor: NAVY,
            '&:hover': { bgcolor: '#1e2530' },
            fontWeight: 600,
            fontSize: 15,
          }}
        >
          {primaryLabel}
        </Button>
        <Button
          variant="text"
          fullWidth
          onClick={() => navigate(-1)}
          sx={{ color: 'text.secondary', fontWeight: 500 }}
        >
          Go back
        </Button>
      </Box>
      {/* Help */}
      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ mt: 5, textAlign: 'center' }}
      >
        Need help?{' '}
        <a
          href="mailto:support@salonlogic.co.uk"
          style={{ color: 'inherit', textDecoration: 'underline' }}
        >
          support@salonlogic.co.uk
        </a>
      </Typography>
    </Box>
  );
};
export default Unauthorized;